import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { getUserData } from 'helpers/Auth'
import { shiftsAPI, type ActiveShift, type CurrentMeta } from 'api/shifts'
import * as posStore from 'store/posStore'

export function resolveAuthRole(): string | undefined {
 const d = getUserData() as { user?: { role?: string }; role?: string } | undefined
 return d?.user?.role ?? d?.role
}

export function resolveSelectedBranchId(): string | undefined {
 const selected = typeof localStorage !== 'undefined' ? localStorage.getItem('selected_branch_id') : null
 return selected || getUserData()?.current_branch_id || undefined
}

export function shiftAppliesToSelectedBranch(
 shift: ActiveShift | null | undefined,
 eventBranchId?: string | null,
): boolean {
 const selected = resolveSelectedBranchId()
 if (!selected) return false
 const shiftBranch = shift?.branch_id ?? eventBranchId
 if (!shiftBranch) return true
 return String(shiftBranch) === String(selected)
}

interface ShiftContextValue {
 activeShift: ActiveShift | null
 shiftMeta: CurrentMeta | null
 loading: boolean
 error: string | null
 requiresShiftForPos: boolean
 isAdmin: boolean
 refetch: () => Promise<void>
}

const ShiftContext = createContext<ShiftContextValue | null>(null)

export const ShiftProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
 const [activeShift, setActiveShift] = useState<ActiveShift | null>(null)
 const [shiftMeta, setShiftMeta] = useState<CurrentMeta | null>(null)
 const [loading, setLoading] = useState(true)
 const [error, setError] = useState<string | null>(null)
 const refetchGen = useRef(0)

 const refetch = useCallback(async () => {
 const gen = ++refetchGen.current
 const branchId = resolveSelectedBranchId()
 setLoading(true)
 setError(null)

 const isStale = () =>
 gen !== refetchGen.current || branchId !== resolveSelectedBranchId()

 try {
 const res = await shiftsAPI.current(branchId)
 if (isStale()) return
 if (res.status === 'success') {
 setActiveShift(res.data ?? null)
 setShiftMeta(res.meta ?? null)
 await posStore.setCachedActiveShift(res.data ?? null, {
 branchId: res.data?.branch_id ?? branchId,
 userId: getUserData()?.id ?? undefined,
 })
 } else {
 setError(res.message || 'تعذر جلب الوردية')
 setActiveShift(null)
 setShiftMeta(null)
 }
 } catch (e: any) {
 if (isStale()) return
 const msg = e?.message ?? ''
 const status = e?.response?.status
 const apiMessage = e?.response?.data?.message ?? ''
 const isExpectedEmptyShiftState =
 [400, 404, 422].includes(status) &&
 (
 apiMessage.includes('لا توجد وردية') ||
 apiMessage.includes('يجب اختيار فرع') ||
 apiMessage.toLowerCase().includes('branch')
 )
 if (isExpectedEmptyShiftState) {
 setActiveShift(null)
 setShiftMeta(e?.response?.data?.meta ?? null)
 setError(null)
 await posStore.setCachedActiveShift(null, {
 branchId,
 userId: getUserData()?.id ?? undefined,
 })
 return
 }
 const isNetworkErr =
 !e.response ||
 !navigator.onLine ||
 e.code === 'ERR_NETWORK' ||
 msg.includes('Network Error') ||
 msg.includes('Failed to fetch') ||
 msg.includes('timeout')
 if (isNetworkErr) {
 const cached = await posStore.getCachedActiveShift<ActiveShift>(
 branchId,
 { userId: getUserData()?.id ?? undefined }
 )
 if (isStale()) return
 if (cached) {
 setActiveShift(cached)
 setShiftMeta(null)
 setError(null)
 setLoading(false)
 return
 }
 }
 setError(apiMessage || 'تعذر جلب الوردية')
 setActiveShift(null)
 setShiftMeta(null)
 } finally {
 if (gen === refetchGen.current) {
 setLoading(false)
 }
 }
 }, [])

 useEffect(() => {
 refetch()
 }, [refetch])

 useEffect(() => {
 const onFocus = () => {
 refetch()
 }
 window.addEventListener('focus', onFocus)
 return () => window.removeEventListener('focus', onFocus)
 }, [refetch])

 useEffect(() => {
 const onBranchChanged = () => {
 void refetch()
 }
 window.addEventListener('branch:changed', onBranchChanged)
 return () => window.removeEventListener('branch:changed', onBranchChanged)
 }, [refetch])

 useEffect(() => {
 const onShiftCacheUpdated = (ev: Event) => {
 const detail = (ev as CustomEvent<{ shift?: ActiveShift | null; branchId?: string | null }>).detail
 if (detail && 'shift' in detail) {
 if (shiftAppliesToSelectedBranch(detail.shift, detail.branchId)) {
 setActiveShift(detail.shift ?? null)
 }
 return
 }
 void (async () => {
 const branchId = resolveSelectedBranchId()
 const cached = await posStore.getCachedActiveShift<ActiveShift>(branchId, {
 userId: getUserData()?.id ?? undefined,
 })
 if (shiftAppliesToSelectedBranch(cached, branchId)) {
 setActiveShift(cached)
 }
 })()
 }
 window.addEventListener('shift:cache-updated', onShiftCacheUpdated)
 return () => window.removeEventListener('shift:cache-updated', onShiftCacheUpdated)
 }, [])

 const value = useMemo<ShiftContextValue>(
 () => ({
 activeShift,
 shiftMeta,
 loading,
 error,
 requiresShiftForPos: true,
 isAdmin: resolveAuthRole() === 'admin',
 refetch,
 }),
 [activeShift, shiftMeta, loading, error, refetch]
 )

 return <ShiftContext.Provider value={value}>{children}</ShiftContext.Provider>
}

export function useShift(): ShiftContextValue {
 const ctx = useContext(ShiftContext)
 if (!ctx) {
 throw new Error('useShift must be used within ShiftProvider')
 }
 return ctx
}
